import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: string[]; // Array of allowed admin levels
}

export default function ProtectedRoute({ children, requireAuth = true, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // If trying to access login page while already authenticated, redirect to appropriate dashboard
  if (location.pathname === '/login' && isAuthenticated && !isLoading) {
    if (user?.adminLevel === 'BRANCH_MANAGER') {
      return <Navigate to="/branch-manager-dashboard" replace />;
    } else if (user?.adminLevel === 'OWNER') {
      return <Navigate to="/owner-dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // If authentication is still loading, show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not authenticated, redirect to login
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check role-based access control
  if (allowedRoles && user?.adminLevel && !allowedRoles.includes(user.adminLevel)) {
    // Redirect unauthorized users to their appropriate dashboard
    if (user?.adminLevel === 'BRANCH_MANAGER') {
      return <Navigate to="/branch-manager-dashboard" replace />;
    } else if (user?.adminLevel === 'OWNER') {
      return <Navigate to="/owner-dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // If we reach here, render the children
  return <>{children}</>;
}
